import {InputRect} from '../../TemplateEditor/FillMode/fieldList';

/** How close (in RENDERED px) the moved element's centre has to come to the
 *  board's centre line before it snaps. Screen px, not design units: the aid has
 *  to feel the same at 40% and at 300% zoom, which is why every caller divides
 *  by the current scale. */
export const SNAP_THRESHOLD_PX = 8;

/** Raw pointer displacement (RENDERED px) below which a gesture is a click, not
 *  a drag. Applied BEFORE snapping on purpose: a 2px twitch that happens to sit
 *  next to a centre line must stay a click rather than becoming a centring move. */
export const DRAG_THRESHOLD_PX = 3;

export interface SnapArgs {
    /** The grabbed occurrence, in DESIGN coords — the snap REFERENCE. Other
     *  occurrences of the same field receive the identical delta. */
    rect: InputRect;
    /** Board size in DESIGN units (the rendered scene's size). */
    boardWidth: number;
    boardHeight: number;
    /** Pointer displacement since pointerdown, in RENDERED px. */
    rawDx: number;
    rawDy: number;
    /** Rendered px per design unit. */
    scale: number;
    snapEnabled: boolean;
    /** Alt held on THIS event — a live bypass, read per pointermove/pointerup. */
    altKey: boolean;
}

export interface SnapResult {
    /** Delta in DESIGN units, snapping already applied. */
    dx: number;
    dy: number;
    snappedX: boolean;
    snappedY: boolean;
}

/** Turn a raw pointer displacement into the design-coord delta to commit,
 *  pulling the element's centre onto the board's centre X/Y when it lands close
 *  enough. The two axes are independent — an element can be centred
 *  horizontally while free vertically, which is the common case for a headline.
 *
 *  Snap-to-centre is a one-time positioning AID, not a stored constraint: it
 *  decides the number recorded now, and a later Figma board-size change composes
 *  offsets and may un-centre the element. */
export const computeSnap = ({
                                rect,
                                boardWidth,
                                boardHeight,
                                rawDx,
                                rawDy,
                                scale,
                                snapEnabled,
                                altKey,
                            }: SnapArgs): SnapResult => {
    // A zero/absent scale would divide the delta into infinity; the caller has
    // no meaningful move to make on an unrendered board.
    if (!scale) return {dx: 0, dy: 0, snappedX: false, snappedY: false};

    let dx = rawDx / scale;
    let dy = rawDy / scale;
    if (!snapEnabled || altKey) return {dx, dy, snappedX: false, snappedY: false};

    const threshold = SNAP_THRESHOLD_PX / scale;
    let snappedX = false;
    let snappedY = false;

    const movedCentreX = rect.x + dx + rect.width / 2;
    const boardCentreX = boardWidth / 2;
    if (boardWidth > 0 && Math.abs(movedCentreX - boardCentreX) <= threshold) {
        dx += boardCentreX - movedCentreX;
        snappedX = true;
    }

    const movedCentreY = rect.y + dy + rect.height / 2;
    const boardCentreY = boardHeight / 2;
    if (boardHeight > 0 && Math.abs(movedCentreY - boardCentreY) <= threshold) {
        dy += boardCentreY - movedCentreY;
        snappedY = true;
    }

    return {dx, dy, snappedX, snappedY};
};

/** Whether a raw pointer displacement (RENDERED px) counts as a drag yet. */
export const passedDragThreshold = (rawDx: number, rawDy: number): boolean =>
    Math.hypot(rawDx, rawDy) >= DRAG_THRESHOLD_PX;
